import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Shield } from 'lucide-react';
import { useClientAuth } from '../contexts/ClientAuthContext';
import logoIcon from '../assets/logo-icon.png';

const API_BASE = '/api/v1/client-portal';

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoComplete?: string;
  helperText?: string;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  autoComplete,
  helperText,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] py-2.5 pl-4 pr-12 text-sm"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          disabled={disabled}
          className="absolute right-0 top-0 flex h-[44px] w-11 items-center justify-center rounded-r-[var(--radius)] text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {helperText ? (
        <p className="mt-1 text-xs text-[var(--muted-foreground)]">{helperText}</p>
      ) : null}
    </div>
  );
};

const ClientForcePasswordChangePage: React.FC = () => {
  const navigate = useNavigate();
  const { getToken, logout, refreshUser } = useClientAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    const token = getToken();
    if (!token) {
      navigate('/client/login', { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Could not change password.');
        return;
      }
      await refreshUser();
      navigate('/client/onboarding', { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--background)] px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <img src={logoIcon} alt="MyMoolah" className="h-10 w-10" />
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)]">MyMoolah Client Portal</p>
          <p className="text-xs text-[var(--muted-foreground)]">Set your password</p>
        </div>
      </div>

      <div className="w-full max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--portal-shadow)]">
        <div className="mb-2 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          <h1 className="text-xl font-semibold text-[var(--foreground)]">Create your password</h1>
        </div>
        <p className="mb-6 text-sm text-[var(--muted-foreground)]">
          For security, replace the temporary password with your own before continuing.
        </p>

        {error ? (
          <div className="mb-4 rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]" role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField
            id="current"
            label="Current (temporary) password"
            value={currentPassword}
            onChange={setCurrentPassword}
            disabled={submitting}
            autoComplete="current-password"
          />
          <PasswordField
            id="new"
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            disabled={submitting}
            autoComplete="new-password"
            helperText="Min 8 characters, one uppercase letter, one number"
          />
          <PasswordField
            id="confirm"
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            disabled={submitting}
            autoComplete="new-password"
          />
          <button type="submit" disabled={submitting}
            className="min-h-[44px] w-full rounded-[var(--radius)] bg-[var(--primary)] text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-60">
            {submitting ? 'Saving…' : 'Save and continue'}
          </button>
          <button type="button" onClick={() => { logout(); navigate('/client/login'); }}
            className="w-full text-center text-xs text-[var(--muted-foreground)] hover:underline">
            Sign out
          </button>
        </form>

        <div className="mt-6 flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] px-2.5 py-1 text-[11px] text-[var(--muted-foreground)]">
            <Shield className="h-3.5 w-3.5" aria-hidden />
            Banking-grade security
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClientForcePasswordChangePage;
